import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import {
  Col,
  Row,
  Form,
  FormInstance,
  Divider,
  Typography,
  DatePicker,
} from 'antd';
import moment from 'moment';
import { OrganizationSettingQuery } from 'works/graphql/organization-setting';
import { useQuery } from '@common/hooks';
import { ErrorHandler, LoadingState, InfoTooltip } from '@common/components';
import { DYNAMIC_RATES_INPUT_KEYS } from '@scheduling/services';
import { isPastDay } from 'works/legacy/lib/date-helper';
import { UnitRateType } from '@scheduling/graphql';
import { RateCurrencyInput } from './_rate-currency-input';
import { filterFormData } from './_incentives-update-steps-service';
import { VariableRatesInputsTable } from './_variable-rates-inputs-table';

const { Title } = Typography;

const [priceRange, incentiveIncrement] = DYNAMIC_RATES_INPUT_KEYS;

function LabelWithTooltip({
  label,
  tooltipText,
}: {
  label: string;
  tooltipText: string;
}) {
  return (
    <>
      <span>{label}</span>
      <InfoTooltip size="sm" leftOffset={8} title={tooltipText} />
    </>
  );
}

type Props = {
  form: FormInstance;
  setRatesValues: (formData: UnitRateType) => void;
  effectiveDate: string;
  setEffectiveDate: Dispatch<SetStateAction<string>>;
  setEndDate: Dispatch<SetStateAction<string>>;
};

export function IncentivesUpdateRatesSetupStep({
  form,
  setRatesValues,
  effectiveDate,
  setEffectiveDate,
  setEndDate,
}: Props) {
  const { loading, error, data } = useQuery(OrganizationSettingQuery);

  const { defaultIncentiveIncrement, allowCustomIncentiveIncrements } =
    data?.organizationSetting || {};

  const [customIncentiveIncrement, setCustomIncentiveIncrement] = useState(0);

  useEffect(() => {
    setCustomIncentiveIncrement(defaultIncentiveIncrement || 0);
  }, [defaultIncentiveIncrement]);

  if (loading) {
    return <LoadingState />;
  }

  if (error) {
    return <ErrorHandler error={error} log="on-demand-rates-step-three" />;
  }

  function onFormFinish(formRawData: UnitRateType) {
    const filteredData = filterFormData(formRawData);

    setEffectiveDate(formRawData.effectiveDate);
    setEndDate(formRawData.endDate || '');

    setRatesValues(filteredData);
  }

  return (
    <>
      <Form
        className="rates-on-demand-step-three"
        layout="vertical"
        onFinish={onFormFinish}
        form={form}
        initialValues={{
          incentiveIncrement: defaultIncentiveIncrement,
        }}
      >
        <section>
          <Title level={3}>Effective Dates</Title>
          <Row gutter={24}>
            <Col span={4}>
              <Form.Item
                rules={[
                  {
                    required: true,
                    message: 'Please select an Effective Date',
                  },
                ]}
                name="effectiveDate"
                label="Start Date"
              >
                <DatePicker
                  style={{
                    width: '100%',
                  }}
                  format="MM/DD/YYYY"
                  data-testid="effective-date"
                  disabledDate={isPastDay}
                  onChange={(date) => {
                    setEffectiveDate(date?.toISOString() || '');
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                name="endDate"
                label={
                  <LabelWithTooltip
                    label="End Date"
                    tooltipText="If an end date is specified, incentive rates will return to the previously active rate card"
                  />
                }
              >
                <DatePicker
                  style={{
                    width: '100%',
                  }}
                  format="MM/DD/YYYY"
                  data-testid="end-date"
                  disabled={!effectiveDate}
                  defaultPickerValue={moment(effectiveDate)}
                  disabledDate={(date) => moment(date).isBefore(effectiveDate)}
                  placeholder="None"
                />
              </Form.Item>
            </Col>
          </Row>
          <Title level={3}>Dynamic Pricing Settings</Title>
          <Row gutter={24}>
            <Col span={4}>
              <RateCurrencyInput
                name={priceRange}
                increment={customIncentiveIncrement}
                validateIncrement
                label={
                  <LabelWithTooltip
                    label="Price Range"
                    tooltipText="Price Range determines how much prices can be adjusted down from the maximum incentive rate"
                  />
                }
              />
            </Col>
            <Col span={4}>
              <RateCurrencyInput
                name={incentiveIncrement}
                validateIncrement
                label={
                  <LabelWithTooltip
                    label="Price Increment"
                    tooltipText="Price Increment determines how many individual rate offerings are available. For example, $10 range and $1 increment would generate 10 unique incentive rate options"
                  />
                }
                onChange={(value) =>
                  setCustomIncentiveIncrement(value as number)
                }
                disabled={!allowCustomIncentiveIncrements}
              />
            </Col>
          </Row>
        </section>
        <Divider />
        <section>
          <Title level={3}>Incentive Rates</Title>
          <Row gutter={24}>
            <Col span={24}>
              <VariableRatesInputsTable
                increment={customIncentiveIncrement}
                setFields={form.setFieldsValue}
                cleanFields={form.resetFields}
              />
            </Col>
          </Row>
        </section>
      </Form>
      <style jsx>{`
        :global(
          .rates-on-demand-step-three__default-title,
          label.rates-on-demand-step-three__checkbox
        ) {
          font-size: 18px;
          line-height: 24px;
        }
        :global(.rates-on-demand-step-three__default-title) {
          margin-bottom: 16px;
        }
        :global(.rates-on-demand-step-three .ant-divider) {
          margin: 24px 0;
        }
        .inputs-column-label {
          line-height: 24px;
        }
      `}</style>
    </>
  );
}
